import { Typography } from '@eventbrite/marmalade';
import { useIntl, defineMessage } from 'react-intl';
import classNames from 'classnames';
import React from 'react';
import { EventUrgencySignalsData, FormattedEvent, UrgencySignalsKeys } from '../../types/events';
import { getFormattedSalesStatus } from '../EventCardBadge';
import styles from './EventCardUrgencySignals.module.scss';

export type EventCardUrgencySignalsProps = {
    urgencySignals?: EventUrgencySignalsData;
    className?: string;
};

/**
 * Component that displays urgency signals on an event card.
 *
 * Migrated from eb-ui on 2025-01-15 at [29e7183a](https://github.com/eventbrite/eb-ui/tree/29e7183a66c76144e37be399fe2713d4c694c60c/packages/attendee/event-renderer/src/components/EventCardUrgencySignals)
 */
export function EventCardUrgencySignals({
    urgencySignals,
    className,
}: EventCardUrgencySignalsProps) {
    const { formatMessage } = useIntl();
    if (!urgencySignals) {
        return null;
    }
    const urgencySignal = getActiveUrgencySignalData(urgencySignals);

    if (!urgencySignal) {
        return null;
    }

    const { className: containerClassName, label } = urgencySignal;
    return (
        <aside className={className}>
            <div className={classNames(containerClassName, styles['EventCardUrgencySignal'])}>
                <Typography
                    className={styles['EventCardUrgencySignal__label']}
                    variant="body-md-bold"
                >
                    {formatMessage(label)}
                </Typography>
            </div>
        </aside>
    );
}

const urgencySignalsData = {
    [UrgencySignalsKeys.fewTickets]: {
        label: defineMessage({ id: 'Almost full', defaultMessage: 'Almost full' }),
        className: styles['EventCardUrgencySignal--almost-full'],
    },
    [UrgencySignalsKeys.salesEndSoon]: {
        label: defineMessage({ id: 'Sales end soon', defaultMessage: 'Sales end soon' }),
        className: styles['EventCardUrgencySignal--sales-end-soon'],
    },
    [UrgencySignalsKeys.earlyBird]: {
        label: defineMessage({ id: 'Early bird', defaultMessage: 'Early bird' }),
        className: styles['EventCardUrgencySignal--early-access'],
    },
    [UrgencySignalsKeys.popular]: {
        label: defineMessage({ id: 'Popular', defaultMessage: 'Popular' }),
        className: styles['EventCardUrgencySignal--popular'],
    },
    [UrgencySignalsKeys.new]: {
        label: defineMessage({ id: 'Just added', defaultMessage: 'Just added' }),
        className: styles['EventCardUrgencySignal--just-added'],
    },
    [UrgencySignalsKeys.goingFast]: {
        label: defineMessage({ id: 'Going fast', defaultMessage: 'Going fast' }),
        className: styles['EventCardUrgencySignal--going-fast'],
    },
};

const urgencySignalPriority = [
    UrgencySignalsKeys.goingFast,
    UrgencySignalsKeys.fewTickets,
    UrgencySignalsKeys.salesEndSoon,
    UrgencySignalsKeys.earlyBird,
    UrgencySignalsKeys.popular,
    UrgencySignalsKeys.new,
];

function getActiveUrgencySignalData(currentUrgencySignals: EventUrgencySignalsData) {
    const activeUrgencySignal = urgencySignalPriority.find(
        (urgencySignal) => currentUrgencySignals[urgencySignal],
    );
    return activeUrgencySignal ? urgencySignalsData[activeUrgencySignal] : undefined;
}

export function areUrgencySignalsActive(
    event: Pick<FormattedEvent, 'eventSalesStatus' | 'urgencySignals' | 'repeatingInstanceCount'>,
) {
    const formattedSalesStatus = getFormattedSalesStatus(event);

    return !!event.urgencySignals && !formattedSalesStatus;
}

export function getActiveUrgencySignals(
    currentUrgencySignals: EventUrgencySignalsData | undefined,
): UrgencySignalsKeys[] {
    if (!currentUrgencySignals) {
        return [];
    }

    return urgencySignalPriority.filter((urgencySignal) => currentUrgencySignals[urgencySignal]);
}
